import React, { useState, useRef, KeyboardEvent, ClipboardEvent, useEffect } from 'react';
import { supabase } from '../utils/supabase';
//import logo from '../assets/axl.png';
import Logo from '../components/Logo';
import { useModal } from '../context/ModalContext';
import { useRecaptcha } from '../hooks/useRecaptcha';
import { API_URL } from '../config';

const CustomAuth: React.FC = () => {
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [showOtpInput, setShowOtpInput] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const otpInputs = useRef<(HTMLInputElement | null)[]>([]);
  const firstRowRef = useRef<HTMLDivElement>(null);
  const secondRowRef = useRef<HTMLDivElement>(null);

  const { openPitchDeckTips } = useModal();
  const { executeRecaptcha } = useRecaptcha();

  // Gallery items data
  const firstRowItems = [
    {
      id: 1,
      title: "10–12 Slide Max",
      description: "More on YCombinator Tips for Good Decks",
      image: "https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
      url: "https://www.ycombinator.com/library/2u-how-to-build-your-seed-round-pitch-deck",
      buttonText: "View YC Tips"
    },
    {
      id: 2,
      title: "Keep Slides to a Minimum",
      description: "Having too many slides will fatigue investors. Be sure to stick to about 10-12 slides or so to keep things simple and make your pitch stand out!",
      image: "https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
      url: "https://www.ycombinator.com/library/2u-how-to-build-your-seed-round-pitch-deck",
      buttonText: "Read Best Practices"
    },
    {
      id: 3,
      title: "Start with a Bang",
      description: "Make sure when you start your pitch to have a very clear and defined problem statement. Keep it simple and don't over think.",
      image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
      url: "https://www.ycombinator.com/library/2u-how-to-build-your-seed-round-pitch-deck",
      buttonText: "See Examples"
    }
  ];

  const secondRowItems = [
    {
      id: 4,
      title: "10–12 Slide Max",
      description: "More on YCombinator Tips for Good Decks",
      image: "https://images.unsplash.com/photo-1553877522-43269d4ea984?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
      url: "https://www.ycombinator.com/library/2u-how-to-build-your-seed-round-pitch-deck",
      buttonText: "View YC Tips"
    },
    {
      id: 5,
      title: "Keep Slides to a Minimum",
      description: "Having too many slides will fatigue investors. Be sure to stick to about 10-12 slides or so to keep things simple and make your pitch stand out!",
      image: "https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
      url: "https://www.ycombinator.com/library/2u-how-to-build-your-seed-round-pitch-deck",
      buttonText: "Read Best Practices"
    },
    {
      id: 6,
      title: "Start with a Bang",
      description: "Make sure when you start your pitch to have a very clear and defined problem statement. Keep it simple and don't over think.",
      image: "https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
      url: "https://www.ycombinator.com/library/2u-how-to-build-your-seed-round-pitch-deck",
      buttonText: "See Examples"
    }
  ];

  // Add animation effect
  useEffect(() => {
    const animateRows = () => {
      if (firstRowRef.current && secondRowRef.current) {
        const firstRow = firstRowRef.current;
        const secondRow = secondRowRef.current;
        
        // Add CSS animation classes with slower duration
        firstRow.style.animation = 'slideRight 100s linear infinite';
        secondRow.style.animation = 'slideLeft 100s linear infinite';

        // Add the keyframes to the document
        const style = document.createElement('style');
        style.textContent = `
          @keyframes slideRight {
            0% { transform: translateX(0); }
            100% { transform: translateX(-33.33%); }
          }
          @keyframes slideLeft {
            0% { transform: translateX(-33.33%); }
            100% { transform: translateX(0); }
          }
        `;
        document.head.appendChild(style);

        // Add hover event listeners
        const handleMouseEnter = (row: HTMLDivElement) => {
          row.style.animationPlayState = 'paused';
        };

        const handleMouseLeave = (row: HTMLDivElement) => {
          row.style.animationPlayState = 'running';
        };

        firstRow.addEventListener('mouseenter', () => handleMouseEnter(firstRow));
        firstRow.addEventListener('mouseleave', () => handleMouseLeave(firstRow));
        secondRow.addEventListener('mouseenter', () => handleMouseEnter(secondRow));
        secondRow.addEventListener('mouseleave', () => handleMouseLeave(secondRow));

        return () => {
          // Cleanup
          firstRow.style.animation = '';
          secondRow.style.animation = '';
          document.head.removeChild(style);
          // Remove event listeners
          firstRow.removeEventListener('mouseenter', () => handleMouseEnter(firstRow));
          firstRow.removeEventListener('mouseleave', () => handleMouseLeave(firstRow));
          secondRow.removeEventListener('mouseenter', () => handleMouseEnter(secondRow));
          secondRow.removeEventListener('mouseleave', () => handleMouseLeave(secondRow));
        };
      }
    };

    animateRows();
  }, []);

  const scrollToGallery = () => {
    const gallerySection = document.getElementById('gallery-section');
    if (gallerySection) {
      gallerySection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleSendOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Execute reCAPTCHA before checking email
      const recaptchaToken = await executeRecaptcha('CHECK_EMAIL');
      
      if (!recaptchaToken) {
        setError('reCAPTCHA verification failed. Please try again.');
        setLoading(false);
        return;
      }

      // First, check if email is whitelisted
      const formData = new FormData();
      formData.append('email', email);
      formData.append('recaptchaToken', recaptchaToken);

      const whitelistResponse = await fetch(`${API_URL}/post/check_email_whitelist`, {
        method: 'POST',
        body: formData
      });

      if (!whitelistResponse.ok) {
        throw new Error('Failed to verify email eligibility');
      }

      const whitelistData = await whitelistResponse.json();
      
      if (!whitelistData.is_whitelisted) {
        setError('Sorry, we are not taking applications right now. Please check back later or contact us for more information.');
        setLoading(false);
        return;
      }

            // If email is whitelisted, proceed with OTP
            const otpRecaptchaToken = await executeRecaptcha('SEND_OTP');
      
            if (!otpRecaptchaToken) {
              setError('reCAPTCHA verification failed. Please try again.');
              setLoading(false);
              return;
            }
      
            // Send OTP with reCAPTCHA token (only if not a dummy token)
            const otpOptions: any = {};
            if (otpRecaptchaToken !== 'dummy-token') {
              otpOptions.captchaToken = otpRecaptchaToken;
            }
      
            const { error } = await supabase.auth.signInWithOtp({ 
              email,
              options: otpOptions
            });
      
         if (error) {
             console.error('Supabase OTP error:', error);
            setError(error.message || 'Failed to send verification code. Please try again.');
            }  else {
        setSuccess('Code sent! Please check your email.');
        setShowOtpInput(true);
        // Focus the first input box after showing OTP input
        setTimeout(() => {
          otpInputs.current[0]?.focus();
        }, 100);
      }
    } catch (error) {
      console.error('Error in handleSendOTP:', error);
      if (error instanceof Error) {
        setError(error.message);
      } else {
        setError('An error occurred. Please try again.');
      }
    }
    
    setLoading(false);
  };

  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Execute reCAPTCHA before verifying OTP
      const recaptchaToken = await executeRecaptcha('VERIFY_OTP');
      
      if (!recaptchaToken) {
        setError('reCAPTCHA verification failed. Please try again.');
        setLoading(false);
        return;
      }

      const otpString = otp.join('');
      const { data, error } = await supabase.auth.verifyOtp({
        email,
        token: otpString,
        type: 'email',
        options: {
          captchaToken: recaptchaToken
        }
      });

      if (data?.session?.access_token && data?.session?.refresh_token) {
        const sessionRes = await supabase.auth.setSession({
          access_token: data?.session?.access_token,
          refresh_token: data?.session?.refresh_token,
        });
        console.log('setSession.sessionRes', sessionRes);
      }

      if (error) {
        setError(error.message);
      }
    } catch (error) {
      setError('An error occurred. Please try again.');
    }
    
    setLoading(false);
  };

  const handleOtpChange = (index: number, value: string) => {
    if (value.length > 1) {
      // Handle paste
      const chars = value.split('').slice(0, 6);
      const newOtp = [...otp];
      chars.forEach((char, i) => {
        if (index + i < 6) {
          newOtp[index + i] = char;
        }
      });
      setOtp(newOtp);
      // Focus the next empty input or the last input
      const nextIndex = Math.min(index + chars.length, 5);
      otpInputs.current[nextIndex]?.focus();
    } else {
      // Handle single character input
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);
      if (value && index < 5) {
        otpInputs.current[index + 1]?.focus();
      }
    }
  };

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      // Move to previous input on backspace if current input is empty
      otpInputs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').trim();
    if (pastedData.length > 0) {
      handleOtpChange(0, pastedData);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#f7f7f7]">
      <header className="w-full bg-black">
        <div className="px-8">
          <div className="flex items-center h-12">
            <a href="/" className="flex-shrink-0">
            <Logo background="light" className="h-4 w-auto" />
            </a>
          </div>
        </div>
      </header>

      {/* Login Section - Full Height minus header */}
      <div className="h-[calc(100vh-48px)] flex flex-col items-center justify-center px-4 relative bg-[#f7f7f7]">
        <div className="w-full max-w-md">
          {!showOtpInput ? (
            <>
              <p className="text-black font-bold text-lg mb-2" style={{ fontFamily: "'Gentium Book Plus', serif" }}>
                AXL LABS
              </p>
              <h1 className="text-4xl font-semibold text-black text-left mb-3" style={{ fontFamily: "'Gentium Book Plus', serif" }}>
                What is your email?
              </h1>
              <p className="text-gray-700 text-left mb-8 text-lg" style={{ fontFamily: "'Gentium Book Plus', serif" }}>
                Provide your email to start or continue your AXL pitch deck analysis.
              </p>
              <form onSubmit={handleSendOTP} className="space-y-6">
                <div>
                  <input
                    type="email"
                    placeholder="Enter your email address"
                    className="w-full px-4 py-4 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4f4fa6] bg-white text-[#2a2a4a] border border-gray-200 placeholder-gray-500 transition text-lg" style={{ fontFamily: "'Gentium Book Plus', serif" }}
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                  />
                </div>

                {error && <div className="text-red-400 text-sm text-center">{error}</div>}
                
                <div className="flex justify-center">
                <button
                  type="submit"
                  className="w-2/3 bg-black text-white font-semibold py-4 rounded-full shadow-lg hover:bg-black transition-all duration-200 text-lg" style={{ fontFamily: "'Gentium Book Plus', serif" }}
                  disabled={loading}
                >
                    {loading ? 'Sending Code...' : 'Continue'}
                  </button>
                </div>
              </form>
            </>
          ) : (
            <>
              <p className="text-black font-bold text-lg mb-2" style={{ fontFamily: "'Gentium Book Plus', serif" }}>
                AXL LABS
              </p>
              <h1 className="text-4xl font-semibold text-black text-left mb-3" style={{ fontFamily: "'Gentium Book Plus', serif" }}>
                Verify Your Email
              </h1>
              <div className="flex gap-3 mb-8">
                <p className="text-black text-left text-lg" style={{ fontFamily: "'Gentium Book Plus', serif" }}>
                  We've sent you a One Time Password to login to the AXL Pitch Deck Analyzer. Enter the code sent to your email here
                </p>
              </div>
              <form onSubmit={handleVerifyOTP} className="space-y-6">
                <div className="flex gap-3 justify-center mb-6">
                  {otp.map((digit, index) => (
                    <input
                      key={index}
                      ref={el => otpInputs.current[index] = el}
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]"
                      maxLength={1}
                      value={digit}
                      onChange={e => handleOtpChange(index, e.target.value)}
                      onKeyDown={e => handleKeyDown(index, e)}
                      onPaste={handlePaste}
                      className="w-14 h-14 text-center text-2xl font-semibold rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4f4fa6] bg-white text-[#2a2a4a] border border-gray-200 transition"
                    />
                  ))}
                </div>

                {error && <div className="text-red-400 text-sm text-center">{error}</div>}
                {success && <div className="text-green-400 text-sm text-center">{success}</div>}

                <div className="flex justify-center">
                <button
                  type="submit"
                  className="w-2/3 bg-black text-white font-semibold py-4 rounded-full shadow-lg hover:bg-black transition-all duration-200 text-lg"
                  disabled={loading || otp.some(digit => !digit)}
                >
                    {loading ? 'Verifying...' : 'Verify Email'}
                  </button>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    setShowOtpInput(false);
                    setOtp(['', '', '', '', '', '']);
                    setError(null);
                    setSuccess(null);
                  }}
                  className="w-full text-gray-700 hover:text-black transition-colors text-lg"
                >
                  Use a different email
                </button>
              </form>
            </>
          )}
        </div>

        {/* "What makes a good deck?" section - positioned at bottom with responsive spacing */}
        <div className="absolute bottom-4 sm:bottom-8 md:bottom-12 left-1/2 transform -translate-x-1/2 flex flex-col items-center">
          <p className="text-black text-lg sm:text-[20px] font-bold font-['Avenir_Next'] text-center mb-3 sm:mb-4" style={{ fontFamily: "'Gentium Book Plus', serif" }}>What makes a good deck?</p>
          <button
            onClick={scrollToGallery}
            className="animate-bounce bg-white p-3 sm:p-4 rounded-full hover:bg-gray-100 transition-all duration-300"
          >
            <svg 
              className="w-3 h-3 sm:w-4 sm:h-4 text-[#23235a]" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M19 14l-7 7m0 0l-7-7m7 7V3"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Gallery Section */}
      <div 
        id="gallery-section" 
        className="w-full bg-[#F5F5F5] py-16 overflow-hidden"
      >
        <div className="w-full px-2">
          {/* Mobile: Carousel-style Single Card Focus */}
          <div className="relative w-full overflow-hidden mb-8 md:hidden px-4">
            <div 
              ref={firstRowRef}
              className="flex gap-6"
              style={{ width: 'max-content' }}
            >
              {[...firstRowItems, ...secondRowItems, ...firstRowItems, ...secondRowItems].map((item, index) => (
                <div 
                  key={`mobile-${index}`}
                  className="flex-shrink-0 w-[calc(100vw-2rem)] h-[400px] relative overflow-hidden shadow-xl rounded-2xl"
                >
                  <img 
                    src={item.image}
                    alt={item.title}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
                  <div className="absolute bottom-0 left-0 p-8 text-white w-full">
                    <h3 className="text-3xl font-bold mb-4 leading-tight">{item.title}</h3>
                    <p className="text-lg opacity-95 whitespace-normal break-words leading-relaxed mb-6 max-w-[90%]">{item.description}</p>
                    <a 
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block px-8 py-4 bg-white text-black rounded-full text-lg font-semibold hover:bg-gray-100 transition-all duration-200 shadow-lg"
                    >
                      {item.buttonText}
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Desktop: First Row */}
          <div className="relative w-full overflow-hidden mb-8 hidden md:block">
            <div 
              ref={firstRowRef}
              className="flex whitespace-nowrap"
              style={{ width: '200%' }}
            >
              {[...firstRowItems, ...firstRowItems].map((item, index) => (
                <div 
                  key={`desktop-first-${index}`}
                  className="inline-block w-[30%] h-[300px] mx-4 relative overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 rounded-lg"
                >
                  <img 
                    src={item.image}
                    alt={item.title}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                  <div className="absolute bottom-0 left-0 p-6 text-white w-[95%]">
                    <h3 className="text-2xl font-bold mb-1">{item.title}</h3>
                    <p className="text-base opacity-90 whitespace-normal break-words text-[14px] leading-tight mb-3">{item.description}</p>
                    <a 
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block px-4 py-2 bg-white text-black rounded-full text-sm font-medium hover:bg-gray-100 transition-all duration-200"
                    >
                      {item.buttonText}
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Second Sliding Row (Desktop Only) */}
          <div className="relative w-full overflow-hidden mb-2 hidden md:block">
            <div 
              ref={secondRowRef}
              className="flex whitespace-nowrap"
              style={{ width: '200%' }}
            >
              {[...secondRowItems, ...secondRowItems].map((item, index) => (
                <div 
                  key={`second-${index}`}
                  className="inline-block w-[30%] h-[300px] mx-4 relative overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 rounded-lg"
                >
                  <img 
                    src={item.image}
                    alt={item.title}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                  <div className="absolute bottom-0 left-0 p-6 text-white w-[95%]">
                    <h3 className="text-2xl font-bold mb-1">{item.title}</h3>
                    <p className="text-base opacity-90 whitespace-normal break-words text-[14px] leading-tight mb-3">{item.description}</p>
                    <a 
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block px-4 py-2 bg-white text-black rounded-full text-sm font-medium hover:bg-gray-100 transition-all duration-200"
                    >
                      {item.buttonText}
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Add More Tips Button */}
          <div className="flex justify-center mt-10">
            <button
              className="inline-block px-8 py-4 bg-black text-white rounded-full text-lg font-medium hover:bg-[#3a3a5a] transition-all duration-200 shadow-lg hover:shadow-xl" style={{ fontFamily: "'Gentium Book Plus', serif" }}
              onClick={openPitchDeckTips}
            >
              More Tips
            </button>
          </div>
        </div>
      </div>

         {/* About AXL Section */}
         <div className="bg-[#f7f7f7] w-full py-16">
        <div className="m-full mx-auto px-4 sm:px-6 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h2 className="text-3xl font-bold text-black" style={{ fontFamily: "'Gentium Book Plus', serif" }}>Who is AXL?</h2>
              <p className="text-lg text-black leading-relaxed" style={{ fontFamily: "'Gentium Book Plus', serif" }}>
                AXL is a venture studio building the next generation of human-centric AI-powered companies in Toronto. Learn more at <a href="https://axl.vc" target="_blank" rel="noopener noreferrer" className="text-black font-semibold hover:opacity-80 transition-opacity underline">axl.vc</a>
              </p>
            </div>
            <div className="relative">
              <div className="aspect-w-16 aspect-h-9 rounded-lg overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-r from-[#57547B] to-[#4a4866] flex items-center justify-center">
                  <svg className="w-24 h-24 text-white/20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="py-4 px-6 text-center bg-[#f7f7f7]">
        <p className="text-xs text-gray-400">
          This site is protected by reCAPTCHA and the Google{' '}
          <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-600">
            Privacy Policy
          </a>{' '}
          and{' '}
          <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-600">
            Terms of Service
          </a>{' '}
          apply.
        </p>
      </footer>
    </div>
  );
};

export default CustomAuth;